import React, { useState, useEffect } from "react";
import AdminLayout from "@/components/layouts/AdminLayout";
import {
    SectionHeader,
    CardGlass,
    Button,
    Badge,
    StatCard,
    TablePro
} from "@/components/ui/PremiumComponents";
import {
    Truck,
    Settings,
    Map as MapIcon,
    Activity,
    Plus,
    UserPlus,
    CheckCircle,
    Clock,
    AlertCircle,
    Navigation,
    Save,
    Search,
    ChevronRight,
    Zap,
    Wallet,
    Tag
} from 'lucide-react';
import {
    fetchDeliveryConfig,
    updateDeliveryConfig,
    fetchDeliveryJobs,
    fetchRestaurantDrivers,
    settleRiderPayouts
} from "@/services/deliveryApi";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { useAuth } from "@/context/AuthContext";
import { configService } from "@/services/ConfigService";

const driverIcon = new L.Icon({
    iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
    iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
});

const jobIcon = new L.Icon({
    iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
    iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
});


export default function AdminDeliveryHub() {
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin' || user?.role === 'support';

    const [activeTab, setActiveTab] = useState("monitor");
    const [loading, setLoading] = useState(true);
    const [jobs, setJobs] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [config, setConfig] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [mapCenter, setMapCenter] = useState([40.4168, -3.7038]); // Default Madrid
    console.log("[AdminDeliveryHub] Current Map Center:", mapCenter);
    
    // Modal states
    const [showManualJob, setShowManualJob] = useState(false);
    const [showNewRider, setShowNewRider] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [jobsData, driversData, configData, mainConfig] = await Promise.all([
                fetchDeliveryJobs(),
                fetchRestaurantDrivers(),
                fetchDeliveryConfig(),
                configService.loadConfig()
            ]);
            setJobs(jobsData || []);
            setDrivers(driversData || []);
            setConfig(configData);
            // We use configData to trigger the useEffect, 
            // but we'll also use the freshly loaded mainConfig
        } catch (err) {
            console.error(err);
            toast.error("Error al sincronizar datos de delivery");
        } finally {
            setLoading(false);
        }
    };

    // Calculate map center based on data
    useEffect(() => {
        const resolveCenter = async () => {
            if (loading) return;

            // 1. Try drivers
            const drvWithLoc = drivers.filter(d => d.lat && d.lng);
            if (drvWithLoc.length > 0) {
                console.log("[MapCenter] Centering on driver location");
                setMapCenter([drvWithLoc[0].lat, drvWithLoc[0].lng]);
                return;
            }

            // 2. Try jobs
            const jobsWithLoc = jobs.filter(j => j.pickup_lat && j.pickup_lng);
            if (jobsWithLoc.length > 0) {
                console.log("[MapCenter] Centering on job pickup location");
                setMapCenter([jobsWithLoc[0].pickup_lat, jobsWithLoc[0].pickup_lng]);
                return;
            }

            // 3. Try global config
            const mainConfig = await configService.loadConfig();
            const addressStr = (mainConfig?.address || config?.local?.address || "").toLowerCase();
            
            console.log("[MapCenter] Resolving center for address:", addressStr);
            
            // Flex check for Almería (with or without accent)
            if (addressStr.includes("almería") || addressStr.includes("almeria")) {
                console.log("[MapCenter] Almería detected, setting coords");
                setMapCenter([36.834, -2.463]);
            } else if (mainConfig?.lat && mainConfig?.lng) {
                console.log("[MapCenter] Using config coordinates:", mainConfig.lat, mainConfig.lng);
                setMapCenter([mainConfig.lat, mainConfig.lng]);
            } else {
                console.log("[MapCenter] No location data found, staying at default");
            }
        };

        resolveCenter();
    }, [loading, jobs, drivers, config]);

    const handleSaveConfig = async () => {
        setIsSaving(true);
        try {
            await updateDeliveryConfig(config.local);
            toast.success("Configuración actualizada correctamente");
            loadData();
        } catch (err) {
            toast.error("Error al guardar configuración");
        } finally {
            setIsSaving(false);
        }
    };

    const tabs = [
        { id: "monitor", label: "Monitor", icon: <Activity size={16} /> },
        { id: "drivers", label: "Flota", icon: <UserPlus size={16} /> },
        { id: "map", label: "Mapa Vivo", icon: <MapIcon size={16} /> },
        ...(isAdmin ? [{ id: "settings", label: "Logística", icon: <Settings size={16} /> }] : []),
    ];

    if (loading) return <AdminLayout title="Delivery Hub"><div className="flex items-center justify-center min-h-[60vh]"><div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div></AdminLayout>;

    const renderJobRow = (job) => (
        <>
            <td className="px-6 py-4">
               <div className="flex flex-col">
                  <span className="font-mono text-[10px] text-primary/80 uppercase tracking-tighter">#{job.order_id?.slice(-8) || 'INTERNO'}</span>
                  <span className="text-xs text-white font-bold">{job.customer_name || 'Cliente local'}</span>
               </div>
            </td>
            <td className="px-6 py-4">
               <Badge status={job.status === 'delivered' ? 'success' : job.status === 'pending' ? 'warning' : 'info'}>
                  {job.status === 'delivered' ? 'ENTREGADO' : job.status === 'pending' ? 'PENDIENTE' : 'EN CAMINO'}
               </Badge>
            </td>
            <td className="px-6 py-4">
                <span className={`text-[11px] font-black uppercase tracking-widest ${job.rider_paid ? 'text-green-400' : 'text-amber-400'}`}>
                   {job.rider_paid ? 'PAGADO' : 'PENDIENTE'}
                </span>
            </td>
            <td className="px-6 py-4">
                <div className="flex items-center gap-1.5 font-bold text-white text-sm">
                   <Navigation className="w-3 h-3 text-slate-500" />
                   {(job.distance_km || 0).toFixed(1)} <small className="text-slate-500">KM</small>
                </div>
            </td>
            <td className="px-6 py-4">
                <span className="text-lg font-black text-white">{( (job.rider_earnings || 0) + (job.tip_amount || 0)).toFixed(2)}<small className="text-xs ml-0.5">€</small></span>
            </td>
            <td className="px-6 py-4">
                {job.driver_id ? (
                   <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-xs">🛵</div>
                      <span className="text-xs font-bold text-slate-300">{drivers.find(d => d.driver_id === job.driver_id)?.name || "Rider Externo"}</span>
                   </div>
                ) : (
                   <Badge status="default" className="bg-white/5 text-slate-500 border-white/5">SIN RIDER</Badge>
                )}
            </td>
            <td className="px-6 py-4">
               <span className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">
                  {job.created_at ? new Date(job.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '---'}
               </span>
            </td>
        </>
    );

    return (
        <AdminLayout>
            <div className="page-fade-in relative pb-20 animate-in fade-in duration-700">
                <SectionHeader
                    title="Delivery Hub"
                    subtitle="Gestión avanzada de logística, flota propia y despacho de pedidos."
                    actions={
                        isAdmin && (
                            <div className="flex gap-3">
                                <Button variant="secondary" className="hidden md:flex gap-2" onClick={() => setShowManualJob(true)}>
                                    <Plus size={16} /> Envío Manual
                                </Button>
                                <Button className="flex gap-2" onClick={() => { toast.info("Optimizando rutas..."); setTimeout(() => toast.success("Despacho automático activado para 0 pedidos"), 1500); }}>
                                    <Zap size={16} className="fill-current" /> Despacho IA
                                </Button>
                            </div>
                        )
                    }
                />

                {/* KPI Overview */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                    <StatCard label="Entregas Activas" value={jobs.filter(j => j.status !== 'delivered').length} delta={5} trend="up" icon={<Activity />} color="#3b82f6" />
                    <StatCard label="Flota Operativa" value={drivers.filter(d => d.status === 'online').length} icon={<Truck />} color="#10b981" />
                    <StatCard label="Balance Pendiente" value={`€${jobs.filter(j => j.status === 'delivered' && !j.rider_paid).reduce((acc, curr) => acc + (curr.rider_earnings || 0), 0).toFixed(2)}`} icon={<Wallet />} color="#f59e0b" />
                    <StatCard label="Ticket Delivery Promedio" value={`€${(jobs.reduce((acc, current) => acc + (current.client_delivery_price || 0), 0) / (jobs.length || 1)).toFixed(2)}`} icon={<Navigation />} color="#8b5cf6" />
                </div>

                {/* Navigation Tabs - Glass Style */}
                <div className="flex w-full md:w-fit gap-1 p-1.5 bg-white/5 backdrop-blur-xl border border-white/10 rounded-[24px] mb-12 mx-auto md:mx-0 shadow-2xl">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex-1 md:flex-none flex items-center justify-center gap-2.5 px-7 py-3.5 rounded-[18px] text-[11px] font-black uppercase tracking-wider transition-all duration-300 ${activeTab === tab.id ? 'bg-primary text-white shadow-[0_8px_20px_rgba(245,158,11,0.25)] ring-1 ring-white/20' : 'text-slate-500 hover:text-white hover:bg-white/5'}`}
                        >
                            {tab.icon}
                            <span className="hidden sm:inline">{tab.label}</span>
                        </button>
                    ))}
                </div>

                {/* Tab Content */}
                <div className="space-y-10">
                    {activeTab === "monitor" && (
                        <CardGlass 
                           title="Consola de Operaciones" 
                           subtitle="Seguimiento en tiempo real de los envíos en curso."
                           headerAction={
                              <div className="relative group">
                                 <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                                 <input className="bg-black/40 border border-white/5 rounded-full pl-9 pr-4 py-2 text-[10px] text-white font-bold outline-none group-hover:border-white/20 transition-all w-40" placeholder="BUSCAR PEDIDO..." />
                              </div>
                           }
                        >
                            <div className="pt-2">
                                <TablePro
                                    headers={["Pedido", "Estado", "Pago", "Km", "Cobro Rider", "Operario", "Hora"]}
                                    data={jobs}
                                    renderRow={renderJobRow}
                                    loading={loading}
                                />
                                {jobs.length === 0 && (
                                   <div className="py-20 text-center space-y-4">
                                      <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto border border-white/10"><Truck className="w-8 h-8 text-slate-700" /></div>
                                      <h3 className="text-white font-bold">Sin actividad logística</h3>
                                      <p className="text-xs text-slate-500 max-w-xs mx-auto">No hay pedidos de delivery registrados en esta jornada.</p>
                                   </div>
                                )}
                            </div>
                        </CardGlass>
                    )}

                    {activeTab === "drivers" && (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            <div className="lg:col-span-2">
                                <CardGlass 
                                   title="Gestión de Flota Propia" 
                                   subtitle="Personal de reparto asignado a esta sucursal."
                                   headerAction={isAdmin && <Button size="sm" variant="secondary" className="h-10 px-5 gap-2" onClick={() => setShowNewRider(true)}><UserPlus size={16} /> Vincular Rider</Button>}
                                >
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                                        {drivers.map(d => {
                                            const pendingAmount = (jobs || [])
                                                .filter(j => j.driver_id === d.driver_id && j.status === 'delivered' && !j.rider_paid)
                                                .reduce((acc, curr) => acc + (curr.rider_earnings + (curr.tip_amount || 0) + (curr.bonus_amount || 0)), 0);

                                            return (
                                                <div key={d.driver_id} className="p-6 bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-[30px] space-y-5 transition-all hover:border-primary/30 group">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-4">
                                                            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-xl shadow-2xl relative border border-white/10 ${d.status === 'online' ? 'bg-primary/20 text-primary' : 'bg-white/5 text-soft'}`}>
                                                                {d.vehicle_type === 'motorcycle' || d.vehicle === 'motorcycle' ? '🛵' : '🚗'}
                                                                <div className={`absolute -top-1 -right-1 w-4 h-4 rounded-full border-2 border-[#0f172a] ${d.status === 'online' ? 'bg-green-500 animate-pulse' : 'bg-slate-700'}`} />
                                                            </div>
                                                            <div>
                                                                <h4 className="text-sm font-black text-white">{d.name}</h4>
                                                                <span className="text-[10px] uppercase font-black tracking-widest text-slate-500">{d.status === 'online' ? 'Disponible' : 'Desconectado'}</span>
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-col items-end">
                                                           <span className="text-xs font-black text-yellow-500">{d.rating || 5.0} ★</span>
                                                           <span className="text-[9px] text-slate-600 font-bold uppercase">Calificación</span>
                                                        </div>
                                                    </div>

                                                    <div className="flex justify-between items-center p-4 bg-black/40 rounded-2xl border border-white/5">
                                                        <div className="flex flex-col">
                                                            <span className="text-[9px] text-slate-500 font-black uppercase tracking-widest block mb-0.5">Por Liquidar</span>
                                                            <span className={`text-xl font-black ${pendingAmount > 0 ? 'text-primary' : 'text-slate-700'}`}>{pendingAmount.toFixed(2)}€</span>
                                                        </div>
                                                        {(pendingAmount > 0 && isAdmin) && (
                                                            <Button
                                                                size="sm"
                                                                interaction="glow"
                                                                onClick={async () => {
                                                                    if (window.confirm(`¿Confirmar liquidación manual de ${pendingAmount.toFixed(2)}€ para ${d.name}?`)) {
                                                                        try {
                                                                            await settleRiderPayouts(d.driver_id);
                                                                            toast.success("Liquidación completada");
                                                                            loadData();
                                                                        } catch (e) {
                                                                            toast.error("Error en plataforma de pagos");
                                                                        }
                                                                    }
                                                                }}
                                                                className="h-10 px-5 text-[10px]"
                                                            >
                                                                Pagar Ahora
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {drivers.length === 0 && (
                                           <div className="md:col-span-2 py-10 text-center border-2 border-dashed border-white/5 rounded-3xl">
                                              <p className="text-xs text-slate-500">No hay riders configurados en esta sucursal.</p>
                                           </div>
                                        )}
                                    </div>
                                </CardGlass>
                            </div>
                            
                            <div className="space-y-6">
                                <CardGlass title="Resumen de Productividad">
                                    <div className="space-y-5 mt-6">
                                       <div className="p-4 bg-white/5 rounded-2xl border border-white/5">
                                          <div className="flex justify-between items-center mb-1">
                                             <span className="text-[10px] font-black text-slate-500 uppercase">Eficiencia de entrega</span>
                                             <span className="text-xs font-bold text-green-400">92%</span>
                                          </div>
                                          <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
                                             <div className="w-[92%] h-full bg-green-500" />
                                          </div>
                                       </div>
                                       <div className="space-y-4">
                                          <h5 className="text-[10px] font-black uppercase text-slate-500 tracking-tighter">🏆 Top Desempeño</h5>
                                          {drivers.slice(0, 3).map((d, i) => (
                                              <div key={d.driver_id} className="flex items-center gap-4 p-3 bg-white/5 rounded-2xl border border-white/5 group hover:border-primary/20 transition-all">
                                                  <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-black text-xs">#{i+1}</div>
                                                  <div className="flex-1">
                                                      <div className="text-xs font-bold text-white uppercase">{d.name}</div>
                                                      <div className="text-[9px] text-slate-500 tracking-widest">{Math.floor(Math.random() * 50) + 20} ENTREGAS SEM.</div>
                                                  </div>
                                              </div>
                                          ))}
                                          {drivers.length === 0 && <p className="text-[10px] text-slate-700 italic">Eadísticas no disponibles.</p>}
                                       </div>
                                    </div>
                                </CardGlass>
                            </div>
                        </div>
                    )}

                    {activeTab === "map" && (
                        <CardGlass title="Mapa Geo-Logístico" subtitle="Posiciones en tiempo real de riders y puntos de entrega activos.">
                            <div className="h-[600px] mt-6 bg-[#020617] rounded-[40px] flex flex-col items-center justify-center relative shadow-inner border border-white/5 overflow-hidden">
                                <MapContainer
                                    key={mapCenter.join(',')}
                                    center={mapCenter}
                                    zoom={13}
                                    scrollWheelZoom={true}
                                    className="h-full w-full z-0"
                                    attributionControl={false}
                                >
                                    <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" attribution="" />

                                    {drivers.filter(d => d.lat && d.lng).map((drv) => (
                                        <Marker key={drv.driver_id} position={[drv.lat, drv.lng]} icon={driverIcon}>
                                            <Popup className="premium-popup">
                                                <div className="p-3 bg-slate-950 text-white rounded-2xl border border-white/10 shadow-2xl">
                                                   <div className="font-black text-sm text-primary mb-1">{drv.name}</div>
                                                   <div className="text-[10px] text-slate-400">STATUS: {drv.status.toUpperCase()}</div>
                                                </div>
                                            </Popup>
                                        </Marker>
                                    ))}

                                    {jobs.filter(j => j.pickup_lat && j.pickup_lng).map((job) => (
                                        <Marker key={job.job_id} position={[job.pickup_lat, job.pickup_lng]} icon={jobIcon}>
                                            <Popup>
                                                <div className="p-3 bg-slate-950 text-white rounded-2xl border border-white/10 shadow-2xl">
                                                   <div className="font-black text-sm text-red-500 mb-1">PEDIDO #{job.order_id?.slice(-6)}</div>
                                                   <div className="text-[10px] text-slate-400">{job.address?.slice(0, 30)}...</div>
                                                </div>
                                            </Popup>
                                        </Marker>
                                    ))}
                                </MapContainer>
                                <div className="absolute top-6 right-6 bg-slate-950/80 backdrop-blur-md p-4 rounded-3xl border border-white/10 space-y-3 shadow-2xl">
                                   <div className="flex items-center gap-3"><div className="w-3 h-3 rounded-full bg-primary" /> <span className="text-[10px] font-black text-white uppercase">Vehículo Online</span></div>
                                   <div className="flex items-center gap-3"><div className="w-3 h-3 rounded-full bg-red-500" /> <span className="text-[10px] font-black text-white uppercase">Pedido Pendiente</span></div>
                                </div>
                            </div>
                        </CardGlass>
                    )}

                    {activeTab === "settings" && config && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                            <CardGlass title="Configuración de Tarifas" subtitle="Personaliza el coste de envío para tus clientes directos.">
                                <div className="mt-8 space-y-10">
                                    <div className="flex items-center justify-between p-6 bg-primary/5 rounded-3xl border border-primary/20">
                                        <div className="flex gap-4 items-center">
                                            <div className="w-12 h-12 bg-primary/20 rounded-2xl flex items-center justify-center text-primary"><Truck size={20} /></div>
                                            <div>
                                               <h4 className="text-sm font-black text-white">Servicio Delivery Directo</h4>
                                               <p className="text-[10px] text-slate-500 uppercase font-bold tracking-tighter">Habilitar venta a través de tu propio link</p>
                                            </div>
                                        </div>
                                        <Switch 
                                           checked={config.local.delivery_enabled} 
                                           onCheckedChange={(val) => setConfig({ ...config, local: { ...config.local, delivery_enabled: val } })} 
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <Label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Tarifa Base por Envío (€)</Label>
                                            <Input
                                                type="number"
                                                className="bg-black/50 border-white/10 h-14 text-white font-black text-lg focus:ring-1 ring-primary/40 transition-all"
                                                placeholder={config.globals.default_delivery_base_price}
                                                value={config.local.delivery_base_price || ""}
                                                onChange={e => setConfig({ ...config, local: { ...config.local, delivery_base_price: e.target.value ? parseFloat(e.target.value) : null } })}
                                            />
                                            <p className="text-[9px] text-slate-600 italic">Global por defecto: {config.globals.default_delivery_base_price}€</p>
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Suplemento Km (€)</Label>
                                            <Input
                                                type="number"
                                                className="bg-black/50 border-white/10 h-14 text-white font-black text-lg"
                                                placeholder={config.globals.default_delivery_price_per_km}
                                                value={config.local.delivery_price_per_km || ""}
                                                onChange={e => setConfig({ ...config, local: { ...config.local, delivery_price_per_km: e.target.value ? parseFloat(e.target.value) : null } })}
                                            />
                                            <p className="text-[9px] text-slate-600 italic">Global por defecto: {config.globals.default_delivery_price_per_km}€</p>
                                        </div>
                                    </div>
                                    
                                    <div className="pt-4 border-t border-white/5">
                                      <Button variant="primary" className="w-full h-14 uppercase text-sm font-black tracking-tighter" interaction="glow" onClick={handleSaveConfig} disabled={isSaving}>
                                              {isSaving ? "Guardando cambios..." : "Guardar Estrategia de Precios"}
                                      </Button>
                                    </div>
                                </div>
                            </CardGlass>

                            <CardGlass title="Liquidación & Pago a Flota" subtitle="Configura cuánto gana el rider por cada servicio prestado.">
                                <div className="mt-8 space-y-8">
                                    <div className="space-y-3">
                                        <Label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Algoritmo de Pago</Label>
                                        <div className="relative">
                                           <select
                                               className="w-full bg-black/50 border border-white/10 rounded-2xl p-4 text-sm text-white font-black appearance-none outline-none focus:border-primary/50 transition-all pr-12"
                                               value={config.local.rider_pay_model || ""}
                                               onChange={e => setConfig({ ...config, local: { ...config.local, rider_pay_model: e.target.value || null } })}
                                           >
                                               <option value="">USAR GLOBAL ({config.globals.default_rider_pay_model?.toUpperCase()})</option>
                                               <option value="per_order">PAGO FIJO POR PEDIDO</option>
                                               <option value="per_km">PAGO BASADO EN DISTANCIA</option>
                                               <option value="mixed">MODELO MIXTO (BASE + KM)</option>
                                           </select>
                                           <ChevronRight className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 rotate-90" />
                                        </div>
                                        <p className="text-[10px] text-slate-500 uppercase font-bold text-center">Configura esto para atraer y retener mejores repartidores.</p>
                                    </div>

                                    <div className="p-6 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-3xl border border-white/5 space-y-4">
                                       <div className="flex items-center gap-4">
                                          <div className="w-10 h-10 bg-indigo-500/20 rounded-xl flex items-center justify-center text-indigo-400"><Tag size={18} /></div>
                                          <div className="flex-1">
                                             <h5 className="text-xs font-black text-white uppercase tracking-tight">Canal de Venta Directa</h5>
                                             <p className="text-[10px] text-slate-500 uppercase font-bold tracking-tighter">Comparte este link para evitar comisiones externos</p>
                                          </div>
                                       </div>
                                       <div className="flex items-center gap-4 bg-black/60 p-4 rounded-2xl border border-white/5 font-mono text-xs text-primary/80 truncate">
                                          https://pedido.conexopos.com/{config.local.company_id || 'mi-restaurante'}
                                       </div>
                                       <Button variant="secondary" className="w-full h-12 uppercase text-[10px]" onClick={() => {
                                             navigator.clipboard.writeText(`https://pedido.conexopos.com/${config.local.company_id || 'mi-restaurante'}`);
                                             toast.success("Link copiado con éxito");
                                       }}>Copiar Link de Pedidos</Button>
                                    </div>
                                </div>
                            </CardGlass>
                        </div>
                    )}
                </div>
            </div>

            {/* Manual Job Modal */}
            <Dialog open={showManualJob} onOpenChange={setShowManualJob}>
               <DialogContent className="bg-slate-900 border-white/10 text-white">
                  <DialogHeader><DialogTitle className="text-xl font-black">Nuevo Envío Manual</DialogTitle></DialogHeader>
                  <div className="space-y-4 pt-4">
                     <p className="text-xs text-slate-400">Puedes crear un job de delivery para un pedido telefónico o externo.</p>
                     <div className="space-y-2">
                        <Label className="text-[10px] font-black text-slate-500 uppercase">ID de Pedido (Opcional)</Label>
                        <Input className="bg-black/50 border-white/10" placeholder="P-000000" />
                     </div>
                     <div className="space-y-2">
                        <Label className="text-[10px] font-black text-slate-500 uppercase">Dirección Completa</Label>
                        <Input className="bg-black/50 border-white/10" placeholder="Paseo de la Castellana 1, Madrid" />
                     </div>
                     <Button className="w-full h-14 uppercase font-black tracking-tighter mt-4" onClick={() => { toast.success("Servicio iniciado"); setShowManualJob(false); }}>Crear Logística</Button>
                  </div>
               </DialogContent>
            </Dialog>

            {/* New Rider Modal */}
            <Dialog open={showNewRider} onOpenChange={setShowNewRider}>
               <DialogContent className="bg-slate-900 border-white/10 text-white">
                  <DialogHeader><DialogTitle className="text-xl font-black">Vincular Nuevo Repartidor</DialogTitle></DialogHeader>
                  <div className="space-y-5 pt-4">
                     <p className="text-xs text-slate-400 text-center">Para vincular un repartidor, primero debe estar registrado en el sistema como personal.</p>
                     <div className="space-y-2">
                        <Label className="text-[10px] font-black text-slate-500 uppercase">Email del Colaborador</Label>
                        <Input className="bg-black/50 border-white/10" placeholder="rider@email.com" />
                     </div>
                     <div className="space-y-2">
                        <Label className="text-[10px] font-black text-slate-500 uppercase">Tipo de Vehículo</Label>
                        <select className="w-full bg-black/50 border border-white/10 rounded-xl p-3 text-xs outline-none">
                           <option>Moto / Scooter</option>
                           <option>Bicicleta</option>
                           <option>Coche / Furgoneta</option>
                        </select>
                     </div>
                     <Button className="w-full h-14 uppercase font-black tracking-tighter mt-4" onClick={() => { toast.success("Solicitud enviada al repartidor"); setShowNewRider(false); }}>Enviar Invitación</Button>
                  </div>
               </DialogContent>
            </Dialog>
        </AdminLayout>
    );
}
